// Subscription status management.
// Caches subscription state in localStorage, refreshes from the backend when online.
// No auth required — subscriptions are tied to device_id via Stripe Customer metadata.

import { getStorageItem, setStorageItem } from './storageService';
import { getDeviceId } from './deviceId';

const STORAGE_KEY = 'madina_subscription';
const GRACE_PERIOD_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

export interface SubscriptionStatus {
  plan: 'free' | 'monthly' | 'annual';
  status: 'inactive' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'expired';
  currentPeriodEnd: number | null;
  trialEnd: number | null;
}

const DEFAULT_STATUS: SubscriptionStatus = {
  plan: 'free',
  status: 'inactive',
  currentPeriodEnd: null,
  trialEnd: null,
};

/** Read cached subscription status from localStorage */
export function getSubscriptionStatus(): SubscriptionStatus {
  return getStorageItem<SubscriptionStatus>(STORAGE_KEY as 'madina_subscription', DEFAULT_STATUS);
}

/** Fetch fresh subscription status from the backend and cache it */
export async function refreshSubscription(): Promise<SubscriptionStatus> {
  try {
    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    const token = localStorage.getItem('madina_sync_token') ?? '';
    const res = await fetch(`${apiUrl}/api/billing/status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Device-Id': getDeviceId(),
      },
    });
    if (!res.ok) return getSubscriptionStatus();
    const json = await res.json() as { ok: boolean; data: SubscriptionStatus };
    if (json.ok && json.data) {
      setStorageItem(STORAGE_KEY as 'madina_subscription', json.data);
      return json.data;
    }
  } catch {
    // Offline or error — use cached
  }
  return getSubscriptionStatus();
}

/** Check if the user can access premium content (Books 2+) */
export function canAccessPremium(): boolean {
  const sub = getSubscriptionStatus();

  if (sub.status === 'active' || sub.status === 'trialing') return true;

  // Grace period: allow access for 3 days after expiry
  if (sub.currentPeriodEnd && sub.status !== 'inactive') {
    const graceEnd = sub.currentPeriodEnd + GRACE_PERIOD_MS;
    if (Date.now() < graceEnd) return true;
  }

  return false;
}

/** Check if a book requires premium */
export function isPremiumContent(book: number): boolean {
  return book > 1;
}

/** Redirect to Stripe Checkout */
export async function startCheckout(plan: 'monthly' | 'annual'): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('madina_sync_token') ?? '';
  const appBase = window.location.origin;

  const res = await fetch(`${apiUrl}/api/billing/checkout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Device-Id': getDeviceId(),
    },
    body: JSON.stringify({
      plan,
      successUrl: `${appBase}/books?subscribed=1`,
      cancelUrl: `${appBase}/books`,
    }),
  });

  const json = await res.json() as { ok: boolean; data: { url: string } };
  if (json.ok && json.data?.url) {
    window.location.href = json.data.url;
  }
}

/** Redirect to Stripe Customer Portal */
export async function openPortal(): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('madina_sync_token') ?? '';
  const appBase = window.location.origin;

  const res = await fetch(`${apiUrl}/api/billing/portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Device-Id': getDeviceId(),
    },
    body: JSON.stringify({ returnUrl: `${appBase}/settings` }),
  });

  const json = await res.json() as { ok: boolean; data: { url: string } };
  if (json.ok && json.data?.url) {
    window.location.href = json.data.url;
  }
}
